import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    // Clear existing socket if user changes
    if (socket) {
      socket.close();
      setSocket(null);
    }

    if (isAuthenticated && user?.id) {
      const socketUrl = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5001';
      const newSocket = io(socketUrl, {
        auth: {
          token: localStorage.getItem('token')
        },
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 2000,
        timeout: 20000,
        forceNew: true
      });

      newSocket.on('connect', () => {
        console.log('Connected to server');
        setConnectionStatus('connected');
        // Join user-specific room with proper format
        newSocket.emit('join', {
          userType: user.userType,
          userId: user.id
        });
        // Also join legacy room for backward compatibility
        newSocket.emit('join-room', user.id);
      });

      newSocket.on('disconnect', () => {
        console.log('Disconnected from server');
        setConnectionStatus('disconnected');
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        setConnectionStatus('error');
      });

      // Listen for order alerts (for pharmacies)
      newSocket.on('order-alert', (data) => {
        console.log('Order alert received:', data);
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'order',
          title: 'New Order Received',
          message: `Order #${data.orderNumber} from ${data.customerName}`,
          timestamp: new Date(),
          data
        }]);
      });

      // Listen for order status updates (for customers and pharmacies)
      newSocket.on('order-status-update', (data) => {
        console.log('Order status update received:', data);
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'status',
          title: 'Order Status Updated',
          message: `Order #${data.orderNumber} is now ${data.status}`,
          timestamp: new Date(),
          data
        }]);
      });

      // Listen for rider location updates (for customers)
      newSocket.on('rider-location', (data) => {
        console.log('Rider location update received:', data);
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'location',
          title: 'Rider Location Update',
          message: `Your delivery rider is on the way`,
          timestamp: new Date(),
          data
        }]);
      });

      // Listen for new order notifications (for pharmacies)
      newSocket.on('new-order', (data) => {
        console.log('New order notification received:', data);
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'new-order',
          title: 'New Order Alert',
          message: `New order #${data.orderNumber} from ${data.customerName}`,
          timestamp: new Date(),
          data
        }]);
      });

      // Listen for general notifications
      newSocket.on('notification', (data) => {
        console.log('Notification received:', data);
        setNotifications(prev => [...prev, {
          id: data._id || Date.now(),
          type: data.type,
          title: data.title,
          message: data.message,
          timestamp: new Date(data.createdAt),
          data: data.data,
          isRead: data.isRead
        }]);
      });

      // Listen for rider assignment notifications (for customers)
      newSocket.on('rider-assigned', (data) => {
        console.log('Rider assigned notification received:', data);
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'rider-assigned',
          title: 'Rider Assigned',
          message: `Your order has been assigned to a rider`,
          timestamp: new Date(),
          data
        }]);
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    } else {
      // Clear socket if not authenticated
      if (socket) {
        socket.close();
        setSocket(null);
      }
    }
  }, [isAuthenticated, user?.id, user?.userType]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      if (socket) {
        socket.close();
      }
    };
  }, []);

  const emitEvent = (event, data) => {
    if (socket) {
      socket.emit(event, data);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const value = {
    socket,
    notifications,
    connectionStatus,
    emitEvent,
    removeNotification,
    clearNotifications
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
